import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPrograms, createProgramService, fetchProgramTypes } from '../services/program.service';
import { ProgramDto, CreateProgramDto, UpdateProgramDto, ProgramTypeDto } from '../types/dtos/program';
import { fetchProgramVersionsDetailed } from '../services/program-version.service';
import { ProgramVersionDto } from '../types/dtos/program';

const PROGRAMS_KEY = 'programs';

export const usePrograms = (organizationId: string) => {
  return useQuery<ProgramDto[], Error>({
    queryKey: [PROGRAMS_KEY, organizationId],
    queryFn: () => fetchPrograms(organizationId),
    enabled: !!organizationId,
  });
};

export const useProgram = (organizationId: string, programId: string) => {
  const service = createProgramService(organizationId);

  return useQuery({
    queryKey: [PROGRAMS_KEY, organizationId, programId],
    queryFn: () => service.findById(programId).then((res) => res.data),
    enabled: !!organizationId && !!programId,
  });
};

export const useCreateProgram = (organizationId: string) => {
  const queryClient = useQueryClient();
  const service = createProgramService(organizationId);

  return useMutation({
    mutationFn: (data: CreateProgramDto) => 
      service.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_KEY, organizationId] });
    }
  });
};

export const useUpdateProgram = (organizationId: string) => {
  const queryClient = useQueryClient();
  const service = createProgramService(organizationId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProgramDto }) =>
      service.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_KEY, organizationId, id] });
    }
  });
};

export const useDeleteProgram = (organizationId: string) => {
  const queryClient = useQueryClient();
  const service = createProgramService(organizationId);

  return useMutation({
    mutationFn: (id: string) => service.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_KEY, organizationId] });
    }
  });
};

export const useAllProgramVersionsDetailed = (organizationId: string) => {

  const { data: programs, isLoading: programsLoading, isError: programsError } = usePrograms(organizationId);

  const versionsQueryEnabled = !!organizationId && !programsLoading && !programsError && !!programs && programs.length > 0;

  return useQuery<ProgramVersionDto[], Error>({
    queryKey: ['allProgramVersionsDetailed', organizationId, programs?.map(p => p.id).join(',')],
    queryFn: async () => {
      
      if (programsLoading || programsError || !programs || programs.length === 0) {
        
        return [];
      }
      
      const versionsPromises = programs.map(program =>
        fetchProgramVersionsDetailed(organizationId, program.id)
      );
      const versionsArrays = await Promise.all(versionsPromises);
      
      return versionsArrays.flat();
    },
    enabled: versionsQueryEnabled,
    select: (data) => {
      const selectedData = data || [];
      return selectedData;
    },
  });
};

/**
 * Hook for fetching available program types for the organization.
 * Used by the program type selector in CreateProgramModal.
 */
export const useProgramTypes = (organizationId?: string) => {
  return useQuery<ProgramTypeDto[], Error>({
    queryKey: ['programTypes', organizationId],
    queryFn: fetchProgramTypes,
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
};